import { capAll, getSubjectColorIndex } from "@/lib/strapi/formatters";
import { Text, Title } from "@mantine/core";
import { Resources } from "@tsTypes/subject-schema";
import { buildFullPath } from "@utils/routing";
import ResourceIcon from "app/components/logos/resources/ResourceIcon";
import Link from "next/link";
import classes from "./ResourceCard.module.css";

export default function ResourceCard({
  subjectNameLower,
  resource,
}: {
  subjectNameLower: string;
  resource: Resources;
}) {
  const subjectColorIndex = getSubjectColorIndex(subjectNameLower);
  const title = getTitleForResourceCard(resource);
  return (
    <Link
      href={buildFullPath({ subject: subjectNameLower, resource: resource })}
      className={classes.wrapper}
      style={
        {
          "--rc-c1": `var(--mantine-color-${subjectColorIndex}-1)`,
          "--rc-c8": `var(--mantine-color-${subjectColorIndex}-8)`,
          "--rc-c9": `var(--mantine-color-${subjectColorIndex}-9)`,
        } as React.CSSProperties
      }
    >
      <ResourceIcon
        resource={resource}
        className={classes.resourceIcon}
        backgroundColor={subjectColorIndex}
      />
      <Title order={3} className={classes.title}>
        {title}
      </Title>
      <Text className={classes.text}>
        {getDescriptionForResourceCard(resource)}
      </Text>
    </Link>
  );
}

function getTitleForResourceCard(resource: Resources) {
  switch (resource) {
    case "videos":
    case "notes":
    case "definitions":
    case "flashcards":
    case "questions":
    case "cram sheets":
    case "topic tests":
    case "mock exams":
    case "test builder":
    case "exam breakdown":
    case "ia examples":
    case "ia criteria":
    case "command terms":
    case "test taker":
      return capAll(resource);
    case "syllabus":
      return "Syllabus Checklist";
    case "exam tests":
      return "Tests";
    case "individual topic test":
      return "Topic Test";
  }
}

function getDescriptionForResourceCard(resource: Resources) {
  switch (resource) {
    case "topic tests":
      return `Attempt pre-built tests using questions seen nowhere else on the site.`;
    case "mock exams":
      return `Generate a random exam-style paper using questions from across the site.`;
    case "test builder":
      return "Create a custom test from your choice of subtopics and difficulties.";
    case "videos":
      return "Understand every concept with concise animated videos, created by experts in their field.";
    case "notes":
      return "Learn properly with notes, curated by international teachers, including formulae, tables, and diagrams.";
    case "definitions":
      return "Familiarise yourself with exam definitions for common and complex terms.";
    case "flashcards":
      return "Memorise quickly with succinct flashcards, built with student-focused features: review, shuffle, and reverse.";
    case "questions":
      return "Practice effectively with MC, short-answer, and multi-part exam questions, all with detailed mark schemes.";
    case "cram sheets":
      return "Maximise your revision during exam periods with condensed sub-topic notes.";
    case "syllabus":
      return "Keep track of your syllabus progress for each topic and sync it site-wide.";
    case "exam breakdown":
      return `Familiarise yourself with the IB exam timings to keep pace on the day.`;
    case "ia examples":
      return "Learn the secrets of the IA with examiner-marked Internal Assessments.";
    case "ia criteria":
      return "Understand how to achieve a 7 in your IA by reviewing the IB marking rubric.";
    case "command terms":
      return "Review specific terms used by examiners to ensure you interpret questions correctly.";
    case "test taker":
      return `Take a custom test or mock exam for the IB syllabus.`;
  }
}
